// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import cstmTable from '../../../../../share/components/vue/PaginationTable';
import loading from '../../../../../share/components/vue/ProcessingIcon';
import apiService from '../../../../../share/util/api/service';
import {
  executePolling
} from '../../../../../share/util/api/polling';
import { apiErrorDefaultAction } from "../../../../../share/util/api/errorAction";
import { OBD_OMT } from '../../../../../constants/usageCountTypes';
import memoPage from '../../../../vue/MemoPage.vue';
import logWrapper from '../../../../../share/logWrapper';
import { pushFunctionList } from "../../../../../router/obd";
import { executeSyncRequest } from '../../../../../share/util/api/syncRequest';
import diagnoseResource from "../../../../../share/util/diagnose";
import constants from "../../../../../common/nodejs/constants";
import { computeObdOmtDTCTableItem } from '../../../../../util/obd/diag';
import { OBD_ONBOARD_MONITOR_TEST_LIMIT } from '../../../../../util/obd/functions';

export default {
  name: "obdOnboardMonitorTest",
  data() {
    return {
      bodyItems: [],
      tableItems: [],
      processing: false,
      measureId: "",
      modalMemo: false,
      modalError: false,
      isMemoDialogLoading: false,
      communicationStandards: constants.UPPER_LAYER_PROTOCOL_SAEJ1979
    };
  },

  props: {
    destinationAPI: { type: String, required: false, default: apiService.OBD },
  },

  components: {
    'cstm-table': cstmTable,
    'processing-view': loading,
    'memo-Page': memoPage,
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
      obdDataId: state => state.obd.obdDataId,
      projectId: state => state.vehicle.vehicleInfo ? state.vehicle.vehicleInfo.vehicle.project.id : null,
    }),

    systemTableColumns() {
      if (this.communicationStandards === constants.UPPER_LAYER_PROTOCOL_SAEJ1979) {
        this.tableItems = [
          { name: 'mid', label: 'fixed_resource.obd_item_mid' },
          { name: 'tid', label: 'fixed_resource.obd_item_tid' },
          { name: 'scaling_id', label: 'fixed_resource.obd_item_scalingid' },
          { name: 'test_value', label: 'fixed_resource.header_data_monitor_table_value', },
          { name: 'unit', label: 'fixed_resource.header_data_monitor_table_unit', },
          { name: 'minimum_test_limit', label: 'fixed_resource.header_data_monitor_table_min', },
          { name: 'maximum_test_limit', label: 'fixed_resource.header_data_monitor_table_max', },
          { name: 'result', label: 'fixed_resource.header_readiness_code_result', }
        ];
      } else {
        this.tableItems = [
          { name: 'status', label: 'message.header_vehicle_dtc_table_status' },
          { name: 'code', label: 'message.header_vehicle_dtc_table_code', originalSortFunc: diagnoseResource.sortFuncCode },
          { name: 'description', label: 'message.header_vehicle_dtc_table_description', useResource: true, originalSortFunc: diagnoseResource.sortFuncDescription },
          { name: 'scaling_id', label: 'fixed_resource.obd_item_scalingid' },
          { name: 'test_value', label: 'fixed_resource.header_data_monitor_table_value', },
          { name: 'unit', label: 'fixed_resource.header_data_monitor_table_unit', },
          { name: 'minimum_test_limit', label: 'fixed_resource.header_data_monitor_table_min', },
          { name: 'maximum_test_limit', label: 'fixed_resource.header_data_monitor_table_max', },
          { name: 'result', label: 'fixed_resource.header_readiness_code_result', }
        ];
      }
      return this.tableItems;
    },

    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断/OBDシステムのうちどの機能なのか判別する
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    },

    /**
     * 結果表示の表示フラグ
     */
    isShowTable() {
      return !this.processing;
    }
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),
    ...mapActions('observation', ['usageCountUp']),

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /**
     * その他のエラー発生時の処理を行う
     * @param {string} action 処理内容
     */
    showUnexpectedError() {
      // 実行不可画面を開く
      this.modalError = true;
    },

    // メモダイアログを表示
    openNoteModal() {
      // モーダルを開く
      this.modalMemo = true;
    },

    // メモ情報の保存
    async memoSave() {
      await this.$refs.memo.save();
    },
    // メモ情報のクリア
    memoClear: function () {
      this.$refs.memo.clear();
    },

    /**
     * メモダイアログを閉じる
     */
    closeNoteModal() {
      this.modalMemo = false;
    },

    /**
     * 再取得を行う
     */
    async clickAgainDataSet() {
      await this.monitorTestDataSet();
    },

    // テーブルデータセット
    async monitorTestDataSet() {
      try {
        this.processing = true;

        const response = await executePolling(
          this,
          // 呼び出したい POST API を実装
          async () =>
            await apiService.postCall(apiService.OBD, `/netApps/${this.netAppId}/obd/${this.obdDataId}/systems/${this.$route.params.systemNo}/onboardMonitorTest`, {}, this.operationScreen),
          // 呼び出したい GET API を実装
          async (postApiResponse) => { return await apiService.getCall(apiService.OBD, `/netApps/${this.netAppId}/requests/${postApiResponse?.data?.request_id}`, this.operationScreen); },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API レスポンスチェックのコールバックはデフォルト処理を使用
          undefined,
          // GET API レスポンスチェックのコールバックはデフォルト処理を使用
          undefined,
          (result) => { this.validateErrorResult(result); },
          (result) => { this.validateErrorResult(result); }
        );
        // OBDシステム利用回数カウントアップ
        this.usageCountUp({ type: OBD_OMT });
        if (response) {
          this.measureId = response.data.results.measure_id;
          await this.getMonitorTestData();
        }

      } catch (error) {
        logWrapper.log('error : ', error);
        this.showUnexpectedError();
      } finally {
        this.processing = false;
      }
    },

    /**
     * 計測データ(オンボードモニタテスト)を取得する
     */
    async getMonitorTestData() {
      let results = null;
      let response = null;
      const params = { last_evaluated_key: null };
      do {
        response = await executeSyncRequest(
          this,
          async () => {
            return await apiService.getParamCall(
              apiService.OBD,
              `/obd/${this.obdDataId}/measures/${this.measureId}/onboardMonitorTest`,
              params,
              this.operationScreen
            );
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // GET API で正常レスポンスが返ってきた際の内容チェックはデフォルト処理を利用
          undefined,
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する
          (result) => { this.validateErrorResult(result); },
        );
        if (response) {
          results = results ? results.concat(response.data.user_data.results) : response.data.user_data.results;
          if (results.length >= OBD_ONBOARD_MONITOR_TEST_LIMIT) break;
          params.last_evaluated_key = response.data.user_data.last_evaluated_key ? JSON.stringify(response.data.user_data.last_evaluated_key) : null;
        }
      } while (params.last_evaluated_key);

      if (results) {
        if (results.length >= OBD_ONBOARD_MONITOR_TEST_LIMIT) results = results.slice(0, OBD_ONBOARD_MONITOR_TEST_LIMIT);
        this.communicationStandards = response.data.user_data.communication_standards;
        this.bodyItems = computeObdOmtDTCTableItem(
          results,
          response.data.user_data.communication_standards === constants.UPPER_LAYER_PROTOCOL_OBD_ON_UDS,
          this
        );
      }
    },

    /**
     * 実行不可画面を閉じる
     */
    async closeWizard() {
      this.$emit("close-wizard");
      pushFunctionList(this, this.$route.params.systemNo);
    },
  },

  async mounted() {
    await this.monitorTestDataSet();
  }
};